/**
 * OnboardingTour — "Product overview" carousel (Commit C, 2026-05-25;
 * re-sequenced 2026-05-31).
 *
 * Six-slide sales-grade storyboard (per [[ux-bar]] and [[robust-productized]]).
 * As of the 2026-05-31 tour re-sequencing it is **collateral, not the first-run
 * experience** — it no longer auto-fires on first login and no longer hands off
 * to the guided tour. The interactive GuidedTour now owns first-run orientation
 * ("where do I go?"); this deck sells the WHY (See / Plan / Catch) and is
 * re-openable on demand from the sidebar's "Product overview" link (and reused
 * as marketing collateral). Opening / closing it touches NO user flags.
 *
 * Storyboard (locked 2026-05-25 with the user):
 *  1. Welcome (slogan: "See the work. Plan the capacity. Catch the risk." — locked 2026-05-23)
 *  2. Get your data in (Excel + Jira, mapping AI, write-back)
 *  3. Avium Signals (proprietary detection engine, Team+)
 *  4. Avium AI Briefing (Claude layer, Business)
 *  5. Plan your sprint (capacity tracker, sprint forecast, workload)
 *  6. Tier comparison + CTA (sourced from planMeta TIER_FEATURE_ROWS so it
 *     can never drift from the actual tier definitions) — kept here as the
 *     conversion surface per the 2026-05-31 decision.
 *
 * Tier badges per slide are neutral (option ii from the proposal); the
 * close slide does the full conversion comparison.
 */
import { useState, useEffect, type ReactNode } from 'react';
import { Eye, LayoutGrid, AlertTriangle } from 'lucide-react';
import {
  PLAN_ORDER, TIER_META, getTierFeatureGroups, type PlanName, type FeatureCell,
} from '../lib/planMeta';
import AviumLogo from './shared/AviumLogo';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Re-sequenced 2026-05-31 (Option A): the carousel is now re-openable
// "Product overview" collateral, NOT the first-run experience. The guided tour
// (GuidedTour) owns first-run orientation. So this component is fully
// decoupled — closing it just dismisses; it no longer marks welcomeSeen /
// tourCompletedAt, and "Done" no longer hands off to the guided tour.
export default function OnboardingTour({ open, onClose }: Props) {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (open) setSlide(0);
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const lastSlide = SLIDES.length - 1;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="brand-header-gradient-accent text-white px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AviumLogo size={32} variant="dark" />
            <div className="font-bold text-base tracking-wide">AVIUM PRODUCT OVERVIEW</div>
            <span className="text-xs font-semibold uppercase tracking-wider bg-card/20 text-white px-2.5 py-1 rounded-full">{slide + 1} of {SLIDES.length}</span>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl leading-none"
            aria-label="Close overview"
          >×</button>
        </div>

        {/* Body — current slide */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {SLIDES[slide].render()}
        </div>

        {/* Footer — slide dots + nav */}
        <div className="border-t border-line bg-wash px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === slide ? 'bg-teal-600 w-6' : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {slide > 0 && (
              <button
                onClick={() => setSlide(s => Math.max(0, s - 1))}
                className="px-5 py-2.5 text-base font-semibold text-ink-muted hover:bg-line rounded-lg transition-colors"
              >
                ← Back
              </button>
            )}
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-base font-medium text-ink-faint hover:text-ink-muted transition-colors"
            >
              Close
            </button>
            {slide < lastSlide ? (
              <button
                onClick={() => setSlide(s => Math.min(lastSlide, s + 1))}
                className="px-6 py-2.5 text-base font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg transition-colors"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-base font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Slide content ───────────────────────────────────────────────────────────

interface Slide {
  render: () => React.ReactNode;
}

const SLIDES: Slide[] = [
  // 1. Welcome
  { render: () => (
    <div className="text-center max-w-4xl mx-auto py-4">
      <div className="flex justify-center mb-6"><AviumLogo size={128} /></div>
      <h2 className="text-6xl font-extrabold text-ink tracking-tight mb-4">Welcome to Avium</h2>
      <div className="text-2xl font-bold text-ink tracking-tight leading-snug mb-3 max-w-2xl mx-auto space-y-1">
        <span className="block">See the work.</span>
        <span className="block">Plan the capacity.</span>
        <span className="block text-brand-spark">Catch the risk.</span>
      </div>
      <p className="text-base text-ink-muted leading-relaxed mb-10 max-w-2xl mx-auto">
        The Avium engine reads your real ticket data — deterministically detects what's at risk, then turns it into a plain-English plan. <span className="font-bold text-brand-primary dark:text-brand-accent">See · Plan · Catch</span>, across every team, every project.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-left mt-8">
        <PillarCard icon={<Eye size={28} strokeWidth={2} className="text-brand-primary-dark dark:text-brand-primary-lighter" />} title="See the work" desc="The real workload across every team and project — one source of truth, no spreadsheet sprawl." />
        <PillarCard icon={<LayoutGrid size={28} strokeWidth={2} className="text-brand-primary-dark dark:text-brand-primary-lighter" />} title="Plan the capacity" desc="Who can take what before sprint planning, not after. Grounded in data, not gut feel." />
        <PillarCard icon={<AlertTriangle size={28} strokeWidth={2} className="text-brand-spark" />} title="Catch the risk" desc="The Avium engine surfaces delivery slips, over-allocation, and stale work before sprint review." />
      </div>
    </div>
  )},

  // 2. Get your data in
  { render: () => (
    <div className="max-w-4xl mx-auto py-2">
      <h2 className="text-4xl font-extrabold text-ink tracking-tight mb-3">See the work — get your data in</h2>
      <p className="text-lg text-ink-muted leading-relaxed mb-7">
        Step one to seeing the work clearly. Two paths in, both under a minute, neither requires a spreadsheet engineer.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-line bg-wash p-6">
          <div className="text-3xl mb-3">📄</div>
          <div className="font-bold text-ink text-lg mb-2">Excel / CSV import</div>
          <p className="text-base text-ink-muted leading-relaxed">
            Drop in any spreadsheet. <strong>AI maps your columns automatically</strong> — review the proposed
            mapping, hit Confirm, done.
          </p>
        </div>
        <div className="rounded-xl border border-teal-200 bg-teal-50 p-6">
          <div className="text-3xl mb-3">🔗</div>
          <div className="font-bold text-ink text-lg mb-2">Connect your Jira instance</div>
          <p className="text-base text-ink-muted leading-relaxed">
            Paste an API token, pick your projects. Avium pulls tickets, sprints, and assignees. AI maps your
            custom fields too.
          </p>
        </div>
      </div>
      <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 px-5 py-4 text-base text-amber-900">
        <strong>Stay in sync.</strong> Avium tracks every change. On Business, push your edits back to Jira
        with one click — status, assignee, sprint, story points, fix versions.
      </div>
      <p className="text-sm text-ink-faint mt-5 italic">
        Finally make data-driven planning decisions instead of arguing about who has bandwidth.
      </p>
    </div>
  )},

  // 3. Avium Signals
  { render: () => (
    <div className="max-w-4xl mx-auto py-2">
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h2 className="text-4xl font-extrabold text-ink tracking-tight">Catch the risk: Avium Signals</h2>
        <span className="text-xs font-bold uppercase tracking-wider bg-teal-100 text-teal-700 px-3 py-1 rounded-full">Proprietary engine</span>
      </div>
      {/* Value-prop hierarchy: short headline sentence + scannable bullets
          (so the brand claims read before the eye reaches the box grid),
          then the compact box grid as supporting detail. The boxes shrunk
          (p-4 → p-3, icon 2xl → lg, title base → sm) so they read as
          supporting evidence, not the headline. User direction 2026-05-24. */}
      <p className="text-lg text-ink-muted leading-relaxed mb-3 font-medium">
        Most tools draw charts. <strong>The Avium engine reads your data and tells you.</strong>
      </p>
      <ul className="text-base text-ink-muted leading-relaxed mb-6 space-y-1.5 list-disc list-inside marker:text-brand-spark">
        <li>Deterministic detection — <strong>no AI guesswork</strong></li>
        <li>12 patterns your current tooling never surfaces</li>
        <li>Every signal grounded in your real tickets — numbers never invented</li>
        <li>Runs in your cloud — <strong>no LLM key required</strong></li>
      </ul>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {SIGNAL_LIST.map(s => (
          <div key={s.name} className="flex items-start gap-2.5 bg-wash rounded-lg p-3 border border-line">
            <span className="text-lg mt-0.5 flex-shrink-0">{s.icon}</span>
            <div className="min-w-0">
              <div className="font-semibold text-ink text-sm mb-0.5">{s.name}</div>
              <div className="text-xs text-ink-muted leading-snug">{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )},

  // 4. Avium AI Briefing
  { render: () => (
    <div className="max-w-4xl mx-auto py-2">
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h2 className="text-4xl font-extrabold text-ink tracking-tight">Plan the response: AI Briefing</h2>
        <span className="text-xs font-bold uppercase tracking-wider bg-teal-100 text-teal-700 px-3 py-1 rounded-full">AI layer</span>
      </div>
      {/* Same hierarchy treatment as slide 3: short headline + bullet
          summary of the value props, then a tighter capability grid as
          supporting evidence. Per user direction 2026-05-24. */}
      <p className="text-lg text-ink-muted leading-relaxed mb-3 font-medium">
        Signals tell you what's happening. <strong>The Briefing tells you what to do.</strong>
      </p>
      <ul className="text-base text-ink-muted leading-relaxed mb-6 space-y-1.5 list-disc list-inside marker:text-brand-spark">
        <li>Deterministic detection <strong>and</strong> grounded narrative — not one or the other</li>
        <li>Every AI claim grounded in a real signal — <strong>numbers never invented</strong></li>
        <li>Five guided capabilities + a natural-language query box</li>
      </ul>
      <div className="space-y-2">
        <AICapability icon="📝" title="Briefing" desc="Top 3 things to act on + items to watch, recomputed every sprint." />
        <AICapability icon="🎯" title="Sprint Risk Forecast" desc="Likelihood the active sprint completes + the drivers + recommendations." />
        <AICapability icon="🧹" title="Data-Quality Fixes" desc="Prioritized cleanup list for the issues hurting your forecasts." />
        <AICapability icon="📋" title="Sprint Review Draft" desc="A first-draft retrospective from the sprint data, ready to edit." />
        <AICapability icon="💬" title="Ask Avium" desc="Natural-language questions against your own data — bugs by epic, capacity by team, anything." />
      </div>
    </div>
  )},

  // 5. Plan your sprint
  { render: () => (
    <div className="max-w-4xl mx-auto py-2">
      <h2 className="text-4xl font-extrabold text-ink tracking-tight mb-3">Plan the capacity</h2>
      <p className="text-lg text-ink-muted leading-relaxed mb-6">
        Grounded in data, not gut feel. Three planning surfaces work together to answer "do we have the right people lined up for the sprint?" <strong>before</strong> planning starts, not after the sprint slips.
      </p>
      <div className="space-y-4">
        <PlanCard
          icon="🗓"
          title="Sprint Forecast Matrix"
          desc="Side-by-side view of who's already committed, who has room, and where over-capacity people are putting delivery at risk. The single most-asked-for view in the app."
        />
        <PlanCard
          icon="📊"
          title="Capacity Tracker"
          desc="Run the engine in hours OR story points — one toggle. Velocity, capacity, committed, and actual numbers from the same source."
        />
        <PlanCard
          icon="🏗"
          title="Assign Work"
          desc="Drag and drop tickets onto people. The system flags conflicts the moment you create one."
        />
      </div>
    </div>
  )},

  // 6. Tier comparison + close
  { render: () => <TierComparisonSlide /> },
];

// ── Sub-components ──────────────────────────────────────────────────────────

// Icons are passed as ReactNode (lucide-react SVG) instead of an emoji string —
// emoji rendered inconsistently across OSes (different shapes, color-emoji
// bleed). Lucide icons honor the className color token so per-pillar
// brand colors (See/Plan in brand-primary-dark, Catch in brand-spark) are
// applied at the call site.
function PillarCard({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-line bg-wash p-5 text-left">
      <div className="mb-3">{icon}</div>
      <div className="font-bold text-ink text-lg mb-1">{title}</div>
      <div className="text-sm text-ink-muted leading-relaxed">{desc}</div>
    </div>
  );
}

function AICapability({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  // Sized down 2026-05-24 (p-4 → p-3, icon 3xl → xl, title lg → base,
  // desc base → sm) so the capability tiles read as supporting evidence
  // rather than competing with the value-prop bullets above.
  return (
    <div className="flex items-start gap-3 bg-gradient-to-r from-teal-50 to-teal-50 border border-teal-100 rounded-lg p-3">
      <span className="text-xl flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="font-bold text-ink text-base mb-0.5">{title}</div>
        <div className="text-sm text-ink-muted leading-snug">{desc}</div>
      </div>
    </div>
  );
}

function PlanCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-4 bg-wash border border-line rounded-lg p-5">
      <span className="text-3xl flex-shrink-0">{icon}</span>
      <div>
        <div className="font-bold text-ink text-lg mb-1">{title}</div>
        <div className="text-base text-ink-muted leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

const SIGNAL_LIST = [
  { icon: '⚠', name: 'Sprint Risk',       desc: 'Progress vs the clock + blockers + over-allocation.' },
  { icon: '🔥', name: 'Over-Allocation',  desc: 'Anyone committed above 105% of capacity, by person.' },
  { icon: '⚖', name: 'Rebalance',         desc: 'Spare capacity in the same department as the over-allocated.' },
  { icon: '📉', name: 'Velocity Drift',   desc: 'Cliffs and trend swings across recent sprints.' },
  { icon: '⏸', name: 'Blocked / Stale',   desc: 'Flagged tickets or no activity past a threshold.' },
  { icon: '🧹', name: 'Data Quality',     desc: 'Weighted score for missing assignee / estimate / sprint / due date.' },
  { icon: '⏳', name: 'Time in Status',   desc: 'Per-phase dwell + currently-stuck list.' },
  { icon: '↩', name: 'Reopen Rate',       desc: 'Quality + acceptance signal — work bouncing back from Done.' },
  { icon: '↔', name: 'Sprint Scope-Change', desc: 'Mid-sprint adds + removes per sprint.' },
  { icon: '🌊', name: 'Flow Efficiency',  desc: 'Active-phase time vs waiting time. Lean staple.' },
  { icon: '🔁', name: 'Hand-off Cost',    desc: 'Assignee changes per ticket — predicts cycle-time + reopens.' },
  { icon: '🕸', name: 'Workflow Sankey',  desc: 'Process mining over the real status paths.' },
];

// ── Tier comparison (slide 6) ───────────────────────────────────────────────

function TierComparisonSlide() {
  const groups = getTierFeatureGroups();
  return (
    <div className="max-w-6xl mx-auto py-2">
      <h2 className="text-4xl font-extrabold text-ink tracking-tight mb-3">Pick the tier that fits</h2>
      <p className="text-lg text-ink-muted leading-relaxed mb-7">
        Start free, upgrade when you outgrow it, cancel any time. Every tier includes the foundational workload tools.
      </p>

      {/* Tier headers — first column wider so feature names have room */}
      <div className="grid grid-cols-[2fr_repeat(4,1fr)] gap-3 text-center mb-4 sticky top-0 bg-card z-10 pb-3">
        <div /> {/* feature-column placeholder */}
        {PLAN_ORDER.map(p => {
          const m = TIER_META[p];
          return (
            <div key={p} className={`rounded-lg py-4 px-2 border-2 ${p === 'business' ? 'border-teal-400 bg-teal-50' : 'border-line bg-wash'}`}>
              <div className="text-base font-bold text-ink">{m.label}</div>
              <div className="text-base text-ink-muted mt-1.5">
                <strong className="text-xl text-ink">{m.price}</strong>{m.priceUnit && <> <span className="text-sm text-ink-faint">{m.priceUnit}</span></>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature rows by group */}
      <div className="space-y-5">
        {groups.map(g => (
          <div key={g.group}>
            <div className="text-sm font-bold uppercase tracking-widest text-ink-faint mb-2">{g.group}</div>
            <div className="rounded-lg border border-line overflow-hidden">
              {g.rows.map((row, idx) => (
                <div key={row.feature} className={`grid grid-cols-[2fr_repeat(4,1fr)] gap-3 items-center text-base ${idx % 2 === 0 ? 'bg-card' : 'bg-wash'} px-4 py-3.5`}>
                  <div className="text-ink">
                    <div className="font-semibold text-base">{row.feature}</div>
                    {row.detail && <div className="text-sm text-ink-faint leading-snug mt-0.5">{row.detail}</div>}
                  </div>
                  {PLAN_ORDER.map(p => <Cell key={p} cell={row.cells[p]} />)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Cell({ cell }: { cell: FeatureCell }) {
  if (cell.included) {
    return (
      <div className="text-center text-base text-emerald-700 font-semibold">
        ✓ {cell.note && <span className="text-sm text-ink-muted font-normal">{cell.note}</span>}
      </div>
    );
  }
  if (cell.roadmap) {
    return (
      <div className="text-center text-sm text-amber-700 italic">Roadmap</div>
    );
  }
  return <div className="text-center text-ink-faint/60 text-base">—</div>;
}

// Helper: handle the cross-cutting plan-name type so consumers don't have to
// re-import PlanName just to map.
export type { PlanName } from '../lib/planMeta';
